import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { FiX, FiClock, FiRefreshCw } from 'react-icons/fi';
import { useLanguage } from '../../context/LanguageContext';
import './Modals.css';
import { getLocalizedCaseDisplay } from '../../data/caseProfiles';
import { useDialogFocus } from '../../hooks/useDialogFocus';

const MAX_MINUTES = 26;

const FALLBACK_PHASES = [
  { name: 'Anamnesis', minutes: 6 },
  { name: 'Physical Exam', minutes: 5 },
  { name: 'Differential & Tests', minutes: 5 },
  { name: 'Evaluation & Management', minutes: 10 }
];

const minutesFromPhase = (phase) => {
  if (phase.minutes) return Number(phase.minutes);
  return Math.max(1, Math.round((phase.duration || 0) / 60000));
};

const TimerModal = ({
  isOpen,
  onClose,
  onStartSession,
  patient,
  patientImage,
  isStarting = false,
  error = '',
  startingLabel,
  phases = FALLBACK_PHASES,
  defaultPhases = FALLBACK_PHASES
}) => {
  const { language, t, phaseName } = useLanguage();
  const [draft, setDraft] = useState(phases);
  const [imageFailed, setImageFailed] = useState(false);
  const localizedPatient = getLocalizedCaseDisplay(patient, language);
  const titleId = useId();
  const firstDurationRef = useRef(null);
  const dialogRef = useDialogFocus(isOpen, { onClose, initialFocusRef: firstDurationRef });

  useEffect(() => {
    if (isOpen) {
      setDraft((phases.length ? phases : FALLBACK_PHASES).map((phase) => ({
        ...phase,
        minutes: minutesFromPhase(phase)
      })));
      setImageFailed(false);
    }
    // Reset when the setup modal opens; keep edits stable during re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const totalMinutes = useMemo(
    () => draft.reduce((sum, phase) => sum + Number(phase.minutes || 0), 0),
    [draft]
  );

  const isValid = draft.every((phase) => Number(phase.minutes) >= 1) && totalMinutes <= MAX_MINUTES;

  const updatePhase = (index, value) => {
    const minutes = Math.max(1, Math.min(MAX_MINUTES, Number(value) || 1));
    setDraft((current) => current.map((phase, phaseIndex) => (
      phaseIndex === index ? { ...phase, minutes } : phase
    )));
  };

  const resetDefaults = () => {
    setDraft((defaultPhases.length ? defaultPhases : FALLBACK_PHASES).map((phase) => ({
      ...phase,
      minutes: minutesFromPhase(phase)
    })));
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay patient-setup-overlay">
      <div className="modal time-modal patient-setup-modal" ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1}>
        <div className="modal-header">
          <div className="modal-title">
            <FiClock size={24} />
            <h2 id={titleId}>{t('setExamTimer')}</h2>
          </div>
          <button className="close-modal" onClick={onClose} aria-label={t('cancel')}>
            <FiX size={24} />
          </button>
        </div>

        <div className="patient-setup-body">
          {localizedPatient && (
            <section className="patient-preview-card">
              <div className="patient-preview-image">
                {!imageFailed && patientImage ? (
                  <img
                    src={patientImage}
                    alt={localizedPatient.name}
                    onError={() => setImageFailed(true)}
                  />
                ) : (
                  <span>{(localizedPatient.name || '?')[0]}</span>
                )}
              </div>
              <div className="patient-preview-copy">
                <span className="patient-preview-kicker">{t('patientProfile')}</span>
                <h3>{localizedPatient.name}</h3>
                <div className="patient-preview-meta">
                  {localizedPatient.age && <span>{localizedPatient.age} {t('years')}</span>}
                  {localizedPatient.gender && <span>{localizedPatient.gender}</span>}
                  {localizedPatient.ethnicity && <span>{localizedPatient.ethnicity}</span>}
                </div>
                {localizedPatient.chiefComplaint && (
                  <div className="patient-presenting-problem">
                    <small>{t('presentingComplaint')}</small>
                    <p>{localizedPatient.chiefComplaint}</p>
                  </div>
                )}
              </div>
            </section>
          )}

          <section className="phase-editor">
            <div className="phase-editor-heading">
              <div>
                <h3>{t('sessionStages')}</h3>
                <p>{t('stageHelp')}</p>
              </div>
              <button type="button" onClick={resetDefaults}>
                <FiRefreshCw /> {t('restoreDefault')}
              </button>
            </div>

            <div className="phase-editor-list">
              {draft.map((phase, index) => (
                <label className="phase-editor-row" key={phase.name}>
                  <span>
                    <b>{String(index + 1).padStart(2, '0')}</b>
                    {phaseName(phase.name)}
                  </span>
                  <span className="phase-minute-input">
                    <input
                      ref={index === 0 ? firstDurationRef : null}
                      type="number"
                      min="1"
                      max={MAX_MINUTES}
                      value={phase.minutes}
                      onChange={(event) => updatePhase(index, event.target.value)}
                    />
                    <em>{t('minutesShort')}</em>
                  </span>
                </label>
              ))}
            </div>

            <div className={`phase-total ${!isValid ? 'invalid' : ''}`}>
              <span>{t('totalSession')}</span>
              <strong>{totalMinutes} / {MAX_MINUTES} {t('minutesShort')}</strong>
            </div>
            {!isValid && <p className="phase-error">{t('reduceStages')}</p>}
          </section>
        </div>

        {error && <p role="alert" className="phase-error">{error}</p>}<div className="patient-setup-actions">
          <button type="button" className="btn-secondary" onClick={onClose}>
            {t('cancel')}
          </button>
          <button
            type="button"
            className="btn-primary start-chatting-btn"
            disabled={!isValid || isStarting}
            onClick={() => onStartSession(draft)}
          >
            {isStarting ? (startingLabel || t('loading')) : t('startSession')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TimerModal;
